import { MUIButton, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useState } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../services/api"
import { FieldRow, FormPage } from "../../../components/FormPage"
import {
  UserExistsGet,
  UserGet,
  UserPostSearch,
  UserPostUser,
} from "../../../models/User"
import { ControllerRendererProps } from "../../../types/reactHookForm"

const userValidationSchema = z.object({
  first: z.string().min(1, "First name is required"),
  last: z.string().min(1, "Last name is required"),
})

const searchValidationSchema = z.object({
  username: z.string().email("Please enter a valid email address"),
})

export function UserCreate() {
  const navigate = useNavigate()
  const returnURL = `/users`
  const [disableInput, setDisableInput] = useState<boolean>(true)
  const [userFound, setUserFound] = useState<boolean>(false)
  const [missingInformation, setMissingInformation] = useState<boolean>(false)

  const {
    control: userControl,
    handleSubmit: handleUserSubmit,
    setValue: setUserValue,
    getValues: getUserValues,
    reset: resetUserForm,
    formState: {
      errors: userErrors,
      isSubmitting: isUserSubmitting,
      isDirty: isUserDirty,
      isValid: isUserValid,
    },
  } = useForm<UserPostUser>({
    resolver: zodResolver(userValidationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    resetUserForm({
      first: "",
      last: "",
    })
  }, [resetUserForm])

  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    setValue: setSearchValue,
    getValues: getSearchValues,
    reset: resetSearchForm,
    formState: {
      errors: searchErrors,
      isSubmitting: isSearchSubmitting,
      isValid: isSearchValid,
    },
  } = useForm<UserPostSearch>({
    resolver: zodResolver(searchValidationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    resetSearchForm({
      username: "",
    })
  }, [resetSearchForm])

  const onUserSubmit = useCallback(
    (values: UserPostUser) => digitalManagerApi
        .post<UserGet>(`/users`, {
          first: values.first,
          last: values.last,
          username: getSearchValues().username,
        })
        .then((res) => {
          if (res.status === StatusCodes.CREATED && res.data.id) {
            toast.success("User added")
            navigate(`/users/${res.data.id}`)
          }
        })
        .catch((err) => {
          if (
            err.response?.status === StatusCodes.BAD_REQUEST &&
            err.response?.data?.errorMessage?.includes("already exists")
          ) {
            toast.warning("A user with this email already exists")
          } else {
            toast.error("Cannot add the user")
          }
        }),
    [getSearchValues, navigate]
  )

  const onSearchSubmit = useCallback(
    (values: UserPostSearch) => {
      digitalManagerApi
        .get<UserExistsGet>(`/users/userByUsername`, {
          params: { username: values.username },
        })
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            if (res.data.alreadyExists) {
              toast.warning("User already exists")
              navigate(`/users/${res.data.id}`)
            } else {
              toast.success("User found on Active Directory")
              setUserValue("first", res.data.first ? res.data.first : "", {
                shouldDirty: true,
                shouldValidate: true,
              })
              setUserValue("last", res.data.last ? res.data.last : "", {
                shouldDirty: true,
                shouldValidate: true,
              })

              if (!res.data.first || !res.data.last) {
                setDisableInput(false)
                setMissingInformation(true)
              } else {
                setUserFound(true)
              }
            }
          }
        })
        .catch((err) => {
          if (err.response?.status === StatusCodes.NOT_FOUND) {
            toast.error("User not found on Active Directory")
          } else {
            toast.warning("Unknown error")
          }
        })
    },
    [setUserValue, navigate]
  )

  const submitForm = useCallback(() => handleUserSubmit(onUserSubmit)(), [handleUserSubmit, onUserSubmit])

  const submitFormSearch = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.code === "Enter" && searchErrors.username === undefined) {
        handleSearchSubmit(onSearchSubmit)()
      }
    },
    [handleSearchSubmit, onSearchSubmit, searchErrors]
  )

  const onChange = useCallback(
    (value: string) => {
      setSearchValue("username", value)
      if (getUserValues().first !== "" || getUserValues().last !== "") {
        setDisableInput(true)
      }
      if (missingInformation) {
        setMissingInformation(false)
      }
      if (userFound) {
        setUserFound(false)
      }
    },
    [setSearchValue, getUserValues, missingInformation, userFound]
  )

  const submitFormSearchOnClick = useCallback(() => {
    handleSearchSubmit(onSearchSubmit)()
  }, [handleSearchSubmit, onSearchSubmit])

  const handleFieldAndExternalChange = useCallback(
    (field: ControllerRenderProps<UserPostSearch, "username">) => (value: string) => {
      field.onChange(value)
      onChange(value)
    },
    [onChange]
  )

  const handleUserTextFieldChange = useCallback(
    (field: ControllerRenderProps<UserPostUser, "first" | "last">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderTextFieldSearchEmail = useCallback(
    ({ field }: ControllerRendererProps<UserPostSearch, "username">) => (
      <MUITextField
        id="username"
        name={field.name}
        placeholder="Search email address on Active Directory"
        size={32}
        value={field.value ?? ""}
        onChange={handleFieldAndExternalChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        onKeyDown={submitFormSearch}
        errors={
          typeof searchErrors.username?.message === "string"
            ? { username: { message: searchErrors.username.message } }
            : undefined
        }
      />
    ),
    [handleFieldAndExternalChange, searchErrors.username, submitFormSearch]
  )

  const renderTextFieldFirstName = useCallback(
    ({ field }: ControllerRendererProps<UserPostUser, "first">) => (
      <MUITextField
        id="first"
        name={field.name}
        type="text"
        size={32}
        value={field.value ?? ""}
        onChange={handleUserTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={disableInput || field.disabled}
        errors={
          typeof userErrors.first?.message === "string"
            ? { first: { message: userErrors.first.message } }
            : undefined
        }
      />
    ),
    [handleUserTextFieldChange, disableInput, userErrors.first]
  )

  const renderTextFieldLastName = useCallback(
    ({ field }: ControllerRendererProps<UserPostUser, "last">) => (
      <MUITextField
        id="last"
        name={field.name}
        type="text"
        size={32}
        value={field.value ?? ""}
        onChange={handleUserTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={disableInput || field.disabled}
        errors={
          typeof userErrors.last?.message === "string"
            ? { last: { message: userErrors.last.message } }
            : undefined
        }
      />
    ),
    [handleUserTextFieldChange, disableInput, userErrors.last]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={submitForm}
      isSubmitting={isUserSubmitting}
      isDirty={isUserDirty}
      isValid={isUserValid}
    >
      <FieldRow label="Email address">
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Controller
            name="username"
            control={searchControl}
            render={renderTextFieldSearchEmail}
          />
          <MUIButton
            iconName="icon_abb_dias icon-abb_search1"
            variant="primary"
            size={40}
            disabled={!isSearchValid}
            isLoading={isSearchSubmitting}
            onClickButton={submitFormSearchOnClick}
          />
        </div>
      </FieldRow>
      {(userFound || missingInformation) && (
        <>
          <FieldRow label="First name">
            {missingInformation ? (
              <Controller
                name="first"
                control={userControl}
                render={renderTextFieldFirstName}
              />
            ) : (
              <div className="text-body1">{getUserValues().first}</div>
            )}
          </FieldRow>
          <FieldRow label="Last name">
            {missingInformation ? (
              <Controller
                name="last"
                control={userControl}
                render={renderTextFieldLastName}
              />
            ) : (
              <div className="text-body1">{getUserValues().last}</div>
            )}
          </FieldRow>
          <FieldRow label="">
            <span className="text-body1">
              {missingInformation
                ? "User found on Active Directory, complete the missing information"
                : "User found on Active Directory, click save to add the user."}
            </span>
          </FieldRow>
        </>
      )}
    </FormPage>
  )
}
